import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import MovieCard from '@/components/base/MovieCard';
import { movies, getMovieById } from '@/mocks/movies';
import { CATEGORY_LABELS } from '@/types/movie';
import useDocumentMeta from '@/hooks/useDocumentMeta';
import NotFound from '@/pages/NotFound';

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const movie = id ? getMovieById(id) : undefined;
  const [notice, setNotice] = useState(false);

  useDocumentMeta({
    title: movie ? `${movie.title} - 妖精视频` : '内容不存在 - 妖精视频',
    description: movie
      ? `${movie.title}（${movie.year}，${movie.region}）是一部${movie.genres.join('、')}作品，豆瓣评分${movie.rating.toFixed(1)}。${movie.synopsis.slice(0, 60)}……`
      : '该影视内容不存在或已下架。',
    keywords: movie ? `妖精视频,${movie.title},${movie.genres.join(',')},${movie.region}影视` : '妖精视频',
  });

  if (!movie) return <NotFound />;
  if (id === undefined) return <NotFound />;

  const related = movies
    .filter((m) => m.id !== movie.id && (m.category === movie.category || m.genres.some((g) => movie.genres.includes(g))))
    .sort((a, b) => {
      const scoreA = (a.category === movie.category ? 1 : 0) + a.genres.filter((g) => movie.genres.includes(g)).length;
      const scoreB = (b.category === movie.category ? 1 : 0) + b.genres.filter((g) => movie.genres.includes(g)).length;
      return scoreB - scoreA;
    })
    .slice(0, 6);

  const infoRows: { label: string; value: string }[] = [
    { label: '上映时间', value: movie.releaseDate },
    { label: '类型', value: movie.genres.join(' / ') },
    { label: '地区', value: movie.region },
    { label: '片长/集数', value: movie.duration },
    { label: '导演', value: movie.director },
    { label: '主演', value: movie.actors.join('、') },
  ].filter((row) => row.value);

  return (
    <SiteLayout>
      <div className="relative w-full h-[300px] md:h-[360px] overflow-hidden bg-primary-950">
        <img
          src={movie.banner || movie.poster}
          alt={`${movie.title} 剧照`}
          title={`${movie.title} - 妖精视频`}
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-primary-950/60 to-primary-950/30"></div>
      </div>

      <div className="w-full px-4 md:px-8">
        <nav className="flex items-center gap-2 text-sm text-foreground-500 -mt-2">
          <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to={`/category/${movie.category}`} className="hover:text-primary-600 transition-colors">
            {CATEGORY_LABELS[movie.category]}
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-900 font-medium truncate">{movie.title}</span>
        </nav>

        <div className="mt-5 grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6 md:gap-8">
          <div className="w-full max-w-[280px] mx-auto lg:mx-0">
            <div className="relative w-full aspect-[2/3] overflow-hidden rounded-lg border border-background-200 bg-background-200">
              <img
                src={movie.poster}
                alt={`${movie.title} 海报`}
                title={`${movie.title} - 妖精视频影视详情`}
                className="w-full h-full object-cover object-top"
              />
            </div>
            <button
              type="button"
              onClick={() => setNotice(true)}
              className="mt-4 w-full whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600 inline-flex items-center justify-center gap-2"
            >
              <i className="ri-play-circle-line text-lg"></i>
              在线观看
            </button>
            {notice && (
              <p className="mt-3 rounded-md bg-accent-50 px-3 py-2 text-xs leading-relaxed text-accent-800">
                演示站点暂未接入真实播放接口，此处仅作示意。敬请期待正式版本。
              </p>
            )}
          </div>

          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="font-heading text-2xl md:text-4xl font-extrabold text-foreground-950">
                {movie.title}
              </h1>
              <span className="rounded-md bg-primary-500 px-2 py-1 text-xs font-semibold text-background-50">
                {CATEGORY_LABELS[movie.category]}
              </span>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2">
              <div className="flex items-baseline gap-1">
                <span className="font-heading text-2xl font-extrabold text-accent-500">
                  {movie.rating.toFixed(1)}
                </span>
                <span className="text-xs text-foreground-500">/ 10 分</span>
              </div>
              <span className="inline-flex items-center gap-1 text-sm text-foreground-600">
                <i className="ri-fire-fill text-accent-500"></i>
                热度 {(movie.heat / 1000).toFixed(1)}k
              </span>
              <span className="inline-flex items-center gap-1 text-sm text-foreground-600">
                <i className="ri-calendar-line"></i>
                {movie.releaseDate}
              </span>
            </div>

            <div className="mt-6 rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
              <h2 className="font-heading text-base font-bold text-foreground-950">作品简介</h2>
              <p className="mt-3 text-sm md:text-base leading-relaxed text-foreground-700">
                {movie.synopsis}
              </p>
            </div>

            <div className="mt-6">
              <h2 className="font-heading text-base font-bold text-foreground-950">基本信息</h2>
              <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                {infoRows.map((row) => (
                  <div key={row.label} className="flex gap-3 text-sm border-b border-background-200/70 pb-2">
                    <dt className="w-20 shrink-0 text-foreground-500">{row.label}</dt>
                    <dd className="text-foreground-900">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <div className="mt-6">
              <h2 className="font-heading text-base font-bold text-foreground-950">相关标签</h2>
              <div className="mt-3 flex flex-wrap gap-2">
                {movie.genres.map((g) => (
                  <Link
                    key={g}
                    to={`/category/${movie.category}?genre=${encodeURIComponent(g)}`}
                    className="rounded-full bg-secondary-100 px-3 py-1.5 text-xs font-medium text-secondary-700 transition-colors hover:bg-secondary-200"
                  >
                    {g}
                  </Link>
                ))}
                {movie.tags.map((t) => (
                  <span key={t} className="rounded-full bg-accent-50 px-3 py-1.5 text-xs font-medium text-accent-700">
                    {t}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to={`/category/${movie.category}`}
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                <i className="ri-arrow-left-line"></i>
                返回{CATEGORY_LABELS[movie.category]}列表
              </Link>
              <Link
                to="/"
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                <i className="ri-home-4-line"></i>
                回到首页
              </Link>
            </div>
          </div>
        </div>

        {related.length > 0 && (
          <section className="mt-14 pb-4">
            <div className="flex items-center gap-2 mb-6 border-b border-background-200 pb-4">
              <span className="w-1 h-6 rounded-full bg-accent-500"></span>
              <h2 className="font-heading text-xl md:text-2xl font-extrabold text-foreground-950">相关推荐</h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
              {related.map((m) => (
                <MovieCard key={m.id} movie={m} />
              ))}
            </div>
          </section>
        )}
      </div>
    </SiteLayout>
  );
}